import React from 'react';
import { useTranslation } from 'next-i18next';
import Metas, {
  LocalizedAlternate,
  MetasProps,
} from '../../../components/Metas';
import OrdersPageComponent, {
  WiredOrdersPageProps as OrdersPageComponentProps,
} from '../../../components/OrdersPage';
import useChangeActiveLocale from '../../../hooks/useChangeActiveLocale';
import JSONSafe from '../../../lib/utils/json';
import { validSupportedLocale } from '../../../lib/utils/locale';
import * as ReverseRoutingHelpers from '../../../lib/utils/reverse-routing';
import HelpCenterInstanceProvider from '../../../providers/HelpCenterInstance';
import InterfaceSettingsProvider from '../../../providers/InterfaceSettings';
import { withDynamic404 } from '../../../wrappers/dynamic-404';
import {
  getHelpCenterChromeServerSideProps,
  getHelpCenterCommonServerSideProps,
  getHelpCenterSearchServerSideProps,
  HelpCenterCommonProps,
} from '../../../wrappers/help-center';
import { withSelfServiceFlows } from '../../../wrappers/ssp';
import withCommonProcessing from '../../../wrappers/withCommonProcessing';

type OrdersPageProps = OrdersPageComponentProps & HelpCenterCommonProps;

const OrdersPage = ({ helpCenter, metas, ...props }: OrdersPageProps) => {
  const { t } = useTranslation();
  const changeActiveLocale = useChangeActiveLocale();

  return (
    <HelpCenterInstanceProvider helpCenter={helpCenter}>
      <Metas title={t('ordersPageMetaTitle')} {...metas} />
      <InterfaceSettingsProvider activeLocale={props.activeLocale}>
        <OrdersPageComponent
          {...props}
          onChangeActiveLocale={changeActiveLocale}
        />
      </InterfaceSettingsProvider>
    </HelpCenterInstanceProvider>
  );
};

export const getServerSideProps = withCommonProcessing<OrdersPageProps>(
  withSelfServiceFlows<OrdersPageProps>(
    async ({ client, helpCenter, locale, host }) => {
      const localizedAlternates: LocalizedAlternate[] = helpCenter.supportedLocales.map(
        (supportedLocale) => ({
          locale: supportedLocale,
          url: ReverseRoutingHelpers.getAbsoluteURL({
            host,
            locale: validSupportedLocale(supportedLocale),
            path: ReverseRoutingHelpers.getPathToHomePage(),
          }),
        }),
      );

      const localizedDefaultURL = ReverseRoutingHelpers.getAbsoluteURL({
        host,
        locale: validSupportedLocale(helpCenter.defaultLocale),
        path: ReverseRoutingHelpers.getPathToHomePage(),
      });

      const canonicalURL = ReverseRoutingHelpers.getAbsoluteURL({
        host,
        locale,
        path: ReverseRoutingHelpers.getPathToHomePage(),
      });

      const metas: Partial<MetasProps> = {
        localizedAlternates,
        localizedDefaultURL,
        canonicalURL,
      };

      return {
        props: JSONSafe({
          ...(await getHelpCenterChromeServerSideProps(
            client,
            helpCenter,
            locale,
          )),
          ...(await getHelpCenterCommonServerSideProps(
            helpCenter,
            locale,
            metas,
          )),
          ...getHelpCenterSearchServerSideProps(helpCenter),
        }),
      };
    },
  ),
);

export default withDynamic404(OrdersPage);
